import { useController, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';

import { useLoginMutation } from '@/store/api/authApi';
import { useAppDispatch } from '@/hooks/redux';
import { setToken } from '@/store/slices/authSlice';
import { openChangePasswordModal, openUnderageModal } from '@/store/slices/modalSlice';
import CustomInput from '@/components/common/CustomInput';
import Checkbox from '@/components/common/Checkbox';
import { getPlatform } from '@/utils';
import type { ApiError } from '@/types/error';
import { toast } from 'sonner';
import { Icons } from '@/utils/icons';
import OTPModal from '@/components/modals/OTPModal';
import { useState } from 'react';

// Validation Schema
const loginSchema = z.object({
  username: z.string().min(1, 'Username required'),
  password: z.string().min(1, 'Password required'),
  agree: z.boolean().refine((val) => val === true, {
    message: 'Please agree Terms and Conditions',
  }),
});

type LoginFormData = z.infer<typeof loginSchema>;

function LoginPage() {
  const [open, setOpen] = useState(false);
  const [verificationData, setverificationData] = useState<{ username: string; type: string }>({
    username: '',
    type: '',
  });
  const [loginMutation, { isLoading }] = useLoginMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { control, handleSubmit } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
      agree: true,
    },
  });

  const {
    field: { value: agreeValue, onChange: onAgreeChange },
  } = useController({
    name: 'agree',
    control,
  });

  //handlers
  const handleInvalid = (errors: { agree?: { message?: string } }) => {
    const message = errors.agree?.message;
    if (message) {
      toast.error(message);
    }
  };
  const handleLogin = async (data: LoginFormData) => {
    try {
      const payload = {
        username: data.username,
        password: data.password,
        device: getPlatform(),
        isTermCondition: data.agree,
      };

      const res = await loginMutation(payload).unwrap();
      if (res?.isTelegramEnabled || res?.mfaRequired) {
        setverificationData({
          username: data.username,
          type: res?.isTelegramEnabled ? 'telegram' : 'number',
        });
        setOpen(true);
      }

      if (res?.accessToken) {
        localStorage.setItem('showWelcomeBanner', 'true');
        dispatch(setToken(res.accessToken));
        if (
          res.passwordChanged !== null &&
          res.passwordChanged !== undefined &&
          res.passwordChanged === false
        ) {
          dispatch(openChangePasswordModal());
          return;
        }
        navigate('/', { replace: true });
      }
    } catch (error) {
      console.log(error);

      const message = (error as ApiError)?.data?.message || 'Invalid username or password';
      toast.error(message);
    }
  };

  return (
    <div className="w-[350px] shadow-md">
      {/* Header */}
      <Link to={'/'} className="block w-full py-2.5 bg-primary">
        <img
          src="/images/logo.png"
          alt=""
          className="h-[60px] lg:h-[85px] 2xl:h-[100px] object-contain mx-auto"
        />
      </Link>

      {/* Form */}
      <form onSubmit={handleSubmit(handleLogin, handleInvalid)} className="p-[30px] bg-[#dfe5e5]">
        {/* Username */}
        <CustomInput<LoginFormData>
          name="username"
          control={control}
          label="Username"
          placeholder="Enter username"
          className="mb-1.5 text-13 py-1"
          inputClassName="bg-transparent border-b font-arial"
        />

        {/* Password */}
        <CustomInput<LoginFormData>
          name="password"
          control={control}
          label="Password"
          placeholder="Enter Password"
          type="password"
          className="text-13"
          inputClassName="bg-transparent border-b font-arial"
        />

        {/* ✅ Agreement Checkbox */}

        <label className="flex items-start gap-2 text-sm font-bold leading-[19px] mb-4 mt-1">
          <Checkbox checked={!!agreeValue} onChange={onAgreeChange} className="mt-1" />
          <span>
            I am at least{' '}
            <button
              type="button"
              onClick={() => dispatch(openUnderageModal())}
              className="text-red-600 hover:text-red-700"
            >
              18 years
            </button>{' '}
            of age and I have read accept and agree to the{' '}
            <a
              href="/terms-and-conditions"
              target="_blank"
              rel="noreferrer"
              aria-label="Terms and Conditions"
              className="text-primary-light-green font-semibold"
            >
              Terms and Conditions
            </a>
            ,{' '}
            <a
              href="/responsible-gaming"
              target="_blank"
              rel="noreferrer"
              aria-label="Responsible Gaming"
              className="text-primary-light-green font-semibold"
            >
              Responsible Gaming
            </a>
            ,{' '}
            <a
              href="https://www.gamcare.org.uk/"
              target="_blank"
              rel="noreferrer"
              aria-label="GamCare"
              className="text-primary-light-green font-semibold"
            >
              GamCare
            </a>
            ,{' '}
            <a
              href="https://gamblingtherapy.org/"
              target="_blank"
              rel="noreferrer"
              aria-label="Gambling Therapy"
              className="text-primary-light-green font-semibold"
            >
              Gambling Therapy
            </a>
          </span>
        </label>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="
            w-full bg-primary hover:bg-green-800 hover:text-white
            text-black font-arial font-medium py-[7px]
            flex items-center justify-center gap-2
            transition disabled:opacity-60
          "
        >
          {isLoading && <Icons.loading className="animate-spin" />}
          Login
        </button>

        {/* ✅ Captcha Policy */}
        <p className="mt-0.5 text-12 text-gray-800 leading-[22px]">
          This site is protected by reCAPTCHA and the Google{' '}
          <a href="https://policies.google.com/privacy" className="text-sky-800/80">
            Privacy Policy
          </a>{' '}
          and{' '}
          <a href="https://policies.google.com/terms" className="text-sky-800/80">
            Terms of Service
          </a>{' '}
          apply.
        </p>
      </form>
      <OTPModal open={open} onClose={() => setOpen(false)} data={verificationData} />
    </div>
  );
}

export default LoginPage;
